import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { LoaderCircle } from 'lucide-react'
import { getCustomerById } from '@/api/customers'
import { applyHistory, getHistoryCustomer, previewHistory, revertHistoryEntry, setHistoryState } from '@/api/history'
import { listInvoices } from '@/api/invoices'
import { getApiErrorMessage } from '@/lib/apiError'
import { getApplyConfirmMessage, getFlagTitle, hasAnythingToApply, isSameEntry, REVERT_CONFIRM_MESSAGE } from '@/lib/history'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { HistoryAppliedCard } from '@/components/history/HistoryAppliedCard'
import { HistoryDeliveriesCard } from '@/components/history/HistoryDeliveriesCard'
import { HistoryForwardCard } from '@/components/history/HistoryForwardCard'
import { HistoryInvoicesCard } from '@/components/history/HistoryInvoicesCard'
import { HistoryOtherCard } from '@/components/history/HistoryOtherCard'
import { HistoryPreviewCard } from '@/components/history/HistoryPreviewCard'
import { HistoryStateBadge } from '@/components/history/HistoryStateBadge'
import { HistoryStatusCard } from '@/components/history/HistoryStatusCard'
import type { Customer, HistoryCustomer, HistoryPreview, HistoryState, HistoryWorklistEntry, Invoice } from '@/api/types'

const LOAD_ERROR_MESSAGE = 'Kunden kunde inte hämtas.'
const PREVIEW_ERROR_MESSAGE = 'Förhandsgranskningen gick inte att göra just nu.'
const APPLY_ERROR_MESSAGE = 'Inläggningen gick inte att göra.'
const STATE_ERROR_MESSAGE = 'Läget kunde inte sparas.'
const REVERT_ERROR_MESSAGE = 'Inläggningen kunde inte ångras.'
const CHECK_BLOCKED_REASON =
  'Går bara när inget ska läggas till, tas bort eller ändras. Lägg in det som står ovan, eller ta bort raderna först.'
const APPLY_NEEDS_PREVIEW_REASON = 'Förhandsgranska först.'
const APPLY_STALE_REASON = 'Posten har ändrats sedan förhandsgranskningen. Förhandsgranska igen.'
const APPLY_PROBLEMS_REASON = 'Förhandsgranskningen visar problem. Lös dem och förhandsgranska igen.'

type StateChoice = Exclude<HistoryState, 'applied'>

export function HistoryCustomerPage(): React.ReactElement {
  const { customerId: customerIdParam } = useParams()
  const customerId = Number(customerIdParam)
  const [historyCustomer, setHistoryCustomer] = useState<HistoryCustomer | null>(null)
  const [customer, setCustomer] = useState<Customer | null>(null)
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [draft, setDraft] = useState<HistoryWorklistEntry | null>(null)
  const [previewedEntry, setPreviewedEntry] = useState<HistoryWorklistEntry | null>(null)
  const [preview, setPreview] = useState<HistoryPreview | null>(null)
  const [enablePostnord, setEnablePostnord] = useState(true)
  const [enableBilling, setEnableBilling] = useState(false)
  const [stateNote, setStateNote] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [isPreviewing, setIsPreviewing] = useState(false)
  const [isApplying, setIsApplying] = useState(false)
  const [isSavingState, setIsSavingState] = useState(false)
  const [isReverting, setIsReverting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const loadAll = async (): Promise<void> => {
    setIsLoading(true)
    setErrorMessage(null)

    try {
      const [history, prod, prodInvoices] = await Promise.all([
        getHistoryCustomer(customerId),
        getCustomerById(customerId),
        listInvoices({ customer_id: customerId })
      ])

      const applied = history.entries.find((entry) => entry.reverted_at === null)
      const shown = history.state === 'applied' && applied ? applied.entry : history.entry

      setHistoryCustomer(history)
      setCustomer(prod)
      setInvoices(prodInvoices)
      setDraft(shown)
      setEnableBilling(shown.billThroughPlatform.length > 0)
      setPreview(null)
      setPreviewedEntry(null)
    } catch (error) {
      setErrorMessage(getApiErrorMessage(error, LOAD_ERROR_MESSAGE))
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    void loadAll()
  }, [customerId])

  const isApplied = historyCustomer?.state === 'applied'
  const isFlagged = historyCustomer?.state === 'flagged'
  const activeEntry = historyCustomer?.entries.find((entry) => entry.reverted_at === null) ?? null
  const hasEarlierEntry = !isApplied && activeEntry !== null
  const isBusy = isPreviewing || isApplying || isSavingState || isReverting
  const isEditingDisabled = isBusy || isApplied
  const isPreviewStale = preview !== null && !isSameEntry(draft, previewedEntry)
  const canApply = preview !== null && preview.problems.length === 0 && !isPreviewStale && preview.entry !== null && !isBusy
  const hasPageData = historyCustomer !== null && customer !== null && draft !== null
  const hasChanges = draft !== null && hasAnythingToApply(draft)
  const canCheck = !isBusy && !hasChanges
  const hasProblems = preview !== null && preview.problems.length > 0
  const applyBlockedReason =
    preview === null ? APPLY_NEEDS_PREVIEW_REASON : isPreviewStale ? APPLY_STALE_REASON : hasProblems ? APPLY_PROBLEMS_REASON : ''
  const checkBlockedReason = hasChanges ? CHECK_BLOCKED_REASON : ''
  const flagText = historyCustomer?.entry.flag ?? (isFlagged ? historyCustomer?.state_note : null) ?? null
  const hasStateNote = Boolean(historyCustomer?.state_note) && !isFlagged
  const hasHeaderBody = flagText !== null || hasStateNote || errorMessage !== null
  const flagTitle = historyCustomer ? getFlagTitle(historyCustomer.entry) : ''

  const handleDraftChange = (entry: HistoryWorklistEntry): void => {
    setDraft(entry)
  }

  const handlePreview = async (): Promise<void> => {
    if (draft === null) return

    setIsPreviewing(true)
    setErrorMessage(null)

    try {
      const result = await previewHistory(customerId, { entry: draft, enablePostnord, enableBilling })

      setPreview(result)
      setPreviewedEntry(draft)
    } catch (error) {
      setErrorMessage(getApiErrorMessage(error, PREVIEW_ERROR_MESSAGE))
    } finally {
      setIsPreviewing(false)
    }
  }

  const handleApply = async (): Promise<void> => {
    if (preview === null || preview.entry === null || draft === null) return
    if (!window.confirm(getApplyConfirmMessage(draft, enablePostnord, preview?.waitingForPostnord ?? false))) return

    setIsApplying(true)
    setErrorMessage(null)

    try {
      await applyHistory(customerId, { entry: preview.entry, enablePostnord, enableBilling })
      await loadAll()
    } catch (error) {
      setErrorMessage(getApiErrorMessage(error, APPLY_ERROR_MESSAGE))
    } finally {
      setIsApplying(false)
    }
  }

  const handleState = async (state: StateChoice): Promise<void> => {
    setIsSavingState(true)
    setErrorMessage(null)

    try {
      const updated = await setHistoryState(customerId, { state, note: stateNote.trim() === '' ? null : stateNote.trim() })

      setHistoryCustomer(updated)
      setStateNote('')
    } catch (error) {
      setErrorMessage(getApiErrorMessage(error, STATE_ERROR_MESSAGE))
    } finally {
      setIsSavingState(false)
    }
  }

  const handleRevert = async (entryId: number): Promise<void> => {
    if (!window.confirm(REVERT_CONFIRM_MESSAGE)) return

    setIsReverting(true)
    setErrorMessage(null)

    try {
      await revertHistoryEntry(entryId)
      await loadAll()
    } catch (error) {
      setErrorMessage(getApiErrorMessage(error, REVERT_ERROR_MESSAGE))
    } finally {
      setIsReverting(false)
    }
  }

  return (
    <div className="space-y-6">
      <Card className="shadow-none border-border/80">
        <CardHeader>
          <CardTitle className="text-3xl font-serif text-primary">{historyCustomer?.name ?? 'Historik'}</CardTitle>
          <CardDescription className="flex flex-wrap items-center gap-2">
            <Link
              to="/historik"
              className="hover:underline"
            >
              ← Alla kunder
            </Link>
            {historyCustomer && (
              <>
                <span>· Kund nr {historyCustomer.customer_id}</span>
                <span>· {historyCustomer.person}</span>
                <Link
                  to={`/customers/${historyCustomer.customer_id}`}
                  className="hover:underline"
                >
                  · Kundkortet
                </Link>
                <HistoryStateBadge state={historyCustomer.state} />
              </>
            )}
          </CardDescription>
        </CardHeader>
        {hasHeaderBody && (
          <CardContent className="space-y-2">
            {flagText && (
              <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
                <p className="font-medium">{flagTitle}</p>
                <p>{flagText}</p>
              </div>
            )}
            {hasStateNote && <p className="text-sm text-muted-foreground">Anteckning: {historyCustomer?.state_note}</p>}
            {errorMessage && <p className="text-sm text-destructive">{errorMessage}</p>}
          </CardContent>
        )}
      </Card>

      {isLoading && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <LoaderCircle className="h-4 w-4 animate-spin" /> Hämtar…
        </p>
      )}

      {isApplied && activeEntry && (
        <HistoryAppliedCard
          entry={activeEntry}
          isEarlierRound={false}
          isReverting={isReverting}
          onRevert={handleRevert}
        />
      )}

      {hasEarlierEntry && activeEntry && (
        <HistoryAppliedCard
          entry={activeEntry}
          isEarlierRound
          isReverting={isReverting}
          onRevert={handleRevert}
        />
      )}

      {hasPageData && (
        <>
          <HistoryStatusCard
            customer={customer}
            entry={draft}
            isDisabled={isEditingDisabled}
            onChange={handleDraftChange}
          />
          <HistoryDeliveriesCard
            shipments={customer.shipments}
            entry={draft}
            isDisabled={isEditingDisabled}
            onChange={handleDraftChange}
          />
          <HistoryInvoicesCard
            invoices={invoices}
            shipments={customer.shipments}
            entry={draft}
            isDisabled={isEditingDisabled}
            onChange={handleDraftChange}
          />
          <HistoryForwardCard
            customer={customer}
            entry={draft}
            isDisabled={isEditingDisabled}
            isApplied={isApplied}
            waitingForPostnord={preview?.waitingForPostnord ?? !customer.subscription?.enable_postnord}
            enablePostnord={enablePostnord}
            enableBilling={enableBilling}
            onChange={handleDraftChange}
            onEnablePostnordChange={setEnablePostnord}
            onEnableBillingChange={setEnableBilling}
          />
          <HistoryOtherCard
            entry={draft}
            isDisabled={isEditingDisabled}
            onChange={handleDraftChange}
          />
        </>
      )}

      {preview && !isApplied && (
        <HistoryPreviewCard
          preview={preview}
          isStale={isPreviewStale}
        />
      )}

      {hasPageData && !isApplied && (
        <Card className="shadow-none border-border/80">
          <CardHeader>
            <CardTitle>Klart att läggas in?</CardTitle>
            <CardDescription>
              Förhandsgranska först. Lägg in skriver allt ovan i plattformen. Stämmer allt redan: markera som kontrollerad.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                className="rounded-none"
                disabled={isBusy}
                onClick={handlePreview}
              >
                {isPreviewing && <LoaderCircle className="h-4 w-4 animate-spin" />}
                Förhandsgranska
              </Button>
              <span
                title={applyBlockedReason}
                className="inline-flex"
              >
                <Button
                  className="rounded-none"
                  disabled={!canApply}
                  onClick={handleApply}
                >
                  {isApplying && <LoaderCircle className="h-4 w-4 animate-spin" />}
                  Lägg in
                </Button>
              </span>
              <span
                title={checkBlockedReason}
                className="inline-flex"
              >
                <Button
                  variant="outline"
                  className="rounded-none"
                  disabled={!canCheck}
                  onClick={() => handleState('checked')}
                >
                  Kontrollerad, inget saknas
                </Button>
              </span>
            </div>
            {hasChanges && <p className="text-xs text-muted-foreground">Kontrollerad: {CHECK_BLOCKED_REASON}</p>}
            <div className="flex flex-wrap items-center gap-2">
              <Input
                value={stateNote}
                onChange={(event) => setStateNote(event.target.value)}
                placeholder="Anteckning (varför)"
                className="max-w-sm"
                disabled={isBusy}
              />
              <Button
                variant="ghost"
                className="rounded-none"
                disabled={isBusy}
                onClick={() => handleState('skipped')}
              >
                Hoppa över
              </Button>
              <Button
                variant="ghost"
                className="rounded-none"
                disabled={isBusy}
                onClick={() => handleState('flagged')}
              >
                Flagga för Ahmad
              </Button>
              {historyCustomer.state !== 'pending' && (
                <Button
                  variant="ghost"
                  className="rounded-none"
                  disabled={isBusy}
                  onClick={() => handleState('pending')}
                >
                  Markera som kvar
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
